import type { ThemeConfig } from "@/types/theme";

export const tailwindCyan: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-cyan-700 dark:text-cyan-400",
    secondary: "text-cyan-900 dark:text-cyan-500",
    accent: "text-sky-600 dark:text-sky-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-cyan-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-cyan-600 dark:bg-cyan-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-cyan-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-cyan-600 dark:bg-cyan-500",
    backgroundSecondaryColor: "bg-cyan-800 dark:bg-cyan-700",
    backgroundAccentColor: "bg-sky-500 dark:bg-sky-400",
    backgroundPrimarySubtle: "bg-cyan-50 dark:bg-cyan-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-cyan-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-cyan-700 dark:text-cyan-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-cyan-600 dark:text-cyan-400",
    iconBackgroundFilled: "bg-cyan-100 dark:bg-cyan-950",
    iconBackgroundGradient: "bg-gradient-to-br from-cyan-500 to-sky-500",
    iconBackgroundDefault: "bg-cyan-50 dark:bg-cyan-950/50",
    iconBorder: "border-cyan-200 dark:border-cyan-800",

    // Border classes
    borderDefault: "border-cyan-200 dark:border-gray-700",
    borderLight: "border-cyan-100 dark:border-gray-800",
    borderStrong: "border-cyan-300 dark:border-gray-600",
    borderPrimary: "border-cyan-700 dark:border-cyan-500",
    borderSecondary: "border-cyan-900 dark:border-cyan-700",
    borderBottom: "border-b border-cyan-200 dark:border-gray-700",
    borderAccent: "border-cyan-600 dark:border-cyan-400",

    // Badge classes
    badgeBackground: "bg-cyan-100 dark:bg-cyan-950",
    badgeText: "text-cyan-700 dark:text-cyan-300",

    // Shadow classes
    shadowPrimary: "shadow-cyan-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-cyan-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(6,182,212,0.30)]", // cyan-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-cyan-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-cyan-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-700 dark:hover:bg-cyan-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-cyan-400 dark:bg-cyan-600",
    textEmphasis: "text-rose-600 dark:text-rose-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-cyan-600 to-cyan-700",
    gradientSecondary: "bg-gradient-to-br from-cyan-700 to-cyan-900",
    gradientAccent: "bg-gradient-to-r from-cyan-500 to-sky-500",
    gradientSubtle:
      "bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant: "bg-gradient-to-r from-cyan-600 via-sky-500 to-cyan-600",
    gradientHero: "bg-gradient-to-br from-cyan-600 via-sky-500 to-cyan-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
